import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';
import { List, FileText, GitBranch, Grid3x3, Sparkles, Loader2 } from 'lucide-react';
import { useExplainFlow } from '@/hooks/useExplainFlow';
import { useOverviewStats } from '@/hooks/useOverviewStats';
import { useTranslation } from '@/i18n/LanguageContext';
import { FlatView } from './overview/FlatView';
import { DetailedView } from './overview/DetailedView';
import { TreeView } from './overview/TreeView';
import { CompactGridView } from './overview/CompactGridView';

export type OverviewViewMode = 'flat' | 'detailed' | 'tree' | 'compact';

interface OverviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
}

export function OverviewDialog({ open, onOpenChange, blocks, connectors, groups }: OverviewDialogProps) {
  const [viewMode, setViewMode] = useState<OverviewViewMode>('flat');
  const { language } = useTranslation();
  const flowExplain = useExplainFlow();

  const handleExplainFlow = useCallback(() => {
    void flowExplain.fetch({
      blocks,
      connectors,
      groups,
      language: language === 'en' ? 'en' : 'sv',
    });
  }, [flowExplain, blocks, connectors, groups, language]);

  // A-019: derived stats extraherat till useOverviewStats-hook.
  const { crossGroupConnectors, resolveName, resolveColor, isEmpty } =
    useOverviewStats({ blocks, connectors, groups });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] w-[900px] h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle>Workflow overview</DialogTitle>
          <DialogDescription className="text-xs">
            {groups.length} groups, {blocks.length} blocks
          </DialogDescription>
          {/* E-001: AI-sammanfattning av hela flödet */}
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleExplainFlow}
              disabled={flowExplain.isLoading || blocks.length === 0}
              data-testid="explain-flow-button"
              className="gap-1.5"
            >
              {flowExplain.isLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              Förklara flödet
            </Button>
            {flowExplain.error && (
              <div className="mt-2 text-xs text-destructive" data-testid="explain-flow-error">
                {flowExplain.error === 'edge-function-not-deployed'
                  ? 'AI-endpoint ej deploy:ad ännu.'
                  : `Kunde inte förklara flödet: ${flowExplain.error}`}
              </div>
            )}
            {flowExplain.summary && !flowExplain.error && (
              <div
                className="mt-2 rounded-md bg-muted/60 p-3 text-sm prose prose-sm max-w-none"
                data-testid="explain-flow-summary"
              >
                <p className="leading-relaxed whitespace-pre-wrap">{flowExplain.summary}</p>
              </div>
            )}
          </div>
        </DialogHeader>

        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as OverviewViewMode)} className="flex flex-col flex-1 min-h-0">
          <div className="px-6 pt-3 pb-0 flex-shrink-0">
            <TabsList className="h-9">
              <TabsTrigger value="flat" className="text-xs gap-1.5">
                <List className="w-3.5 h-3.5" />
                Flat
              </TabsTrigger>
              <TabsTrigger value="detailed" className="text-xs gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Detailed
              </TabsTrigger>
              <TabsTrigger value="tree" className="text-xs gap-1.5">
                <GitBranch className="w-3.5 h-3.5" />
                Tree
              </TabsTrigger>
              <TabsTrigger value="compact" className="text-xs gap-1.5">
                <Grid3x3 className="w-3.5 h-3.5" />
                Compact
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <TabsContent value="flat" className="p-6 pt-4 mt-0">
              {!isEmpty ? (
                <FlatView
                  blocks={blocks}
                  connectors={connectors}
                  groups={groups}
                  crossGroupConnectors={crossGroupConnectors}
                  resolveName={resolveName}
                  resolveColor={resolveColor}
                />
              ) : (
                <div className="text-center text-muted-foreground py-12">
                  No blocks or groups in the workflow yet.
                </div>
              )}
            </TabsContent>

            <TabsContent value="detailed" className="p-6 pt-4 mt-0">
              {!isEmpty ? (
                <DetailedView
                  blocks={blocks}
                  connectors={connectors}
                  groups={groups}
                  crossGroupConnectors={crossGroupConnectors}
                  resolveName={resolveName}
                  resolveColor={resolveColor}
                />
              ) : (
                <div className="text-center text-muted-foreground py-12">
                  No blocks or groups in the workflow yet.
                </div>
              )}
            </TabsContent>

            <TabsContent value="tree" className="p-6 pt-4 mt-0">
              {!isEmpty ? (
                <TreeView
                  blocks={blocks}
                  connectors={connectors}
                  groups={groups}
                  crossGroupConnectors={crossGroupConnectors}
                  resolveName={resolveName}
                  resolveColor={resolveColor}
                />
              ) : (
                <div className="text-center text-muted-foreground py-12">
                  No blocks or groups in the workflow yet.
                </div>
              )}
            </TabsContent>

            <TabsContent value="compact" className="p-6 pt-4 mt-0">
              {!isEmpty ? (
                <CompactGridView
                  blocks={blocks}
                  groups={groups}
                  crossGroupConnectors={crossGroupConnectors}
                  resolveName={resolveName}
                  resolveColor={resolveColor}
                />
              ) : (
                <div className="text-center text-muted-foreground py-12">
                  No blocks or groups in the workflow yet.
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
